import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { NotificationModel } from '../interfaces/calendar-model.interface';
import { Router } from '@angular/router';
import { NOTIFICATION, NOTIFICATION_TYPE } from 'src/app/constants/notifications';

@Component({
  selector: 'app-calendar-schedules-modal',
  templateUrl: './calendar-schedules-modal.component.html',
  styleUrls: ['./calendar-schedules-modal.component.scss']
})
export class CalendarSchedulesModalComponent implements OnInit {

  public readonly NOTIFICATION = NOTIFICATION;

  public readonly NOTIFICATION_TYPE = NOTIFICATION_TYPE;

  public notifications: any[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { notifications: NotificationModel[] },
    public dialogRef: MatDialogRef<CalendarSchedulesModalComponent>,
    private router: Router
  ) {
    if (data && data.notifications) {
      this.notifications = this.processNotifications(data.notifications);
    }
  }

  ngOnInit() {
  }

  public close(): void {
    this.dialogRef.close();
  }

  public goToEntity(notification: NotificationModel): void {
    this.close();
    this.router.navigate([`/details/${notification.related.type}/${notification.related.id}`]);
  }

  public processNotifications(notifications: NotificationModel[]): any[] {
    if (!notifications || notifications.length === 0) {
      return [];
    }

    // Group notifications by date
    const grouped = new Map<string, NotificationModel[]>();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    notifications.forEach(notification => {
      if (!notification.start) {
        return;
      }

      const notificationDate = new Date(notification.start * 1000);
      notificationDate.setHours(0, 0, 0, 0);
      
      let groupLabel: string;
      
      // Check if it's today
      if (notificationDate.getTime() === today.getTime()) {
        groupLabel = 'Today';
      }
      // Check if it's tomorrow
      else if (notificationDate.getTime() === tomorrow.getTime()) {
        groupLabel = 'Tomorrow';
      }
      // Otherwise, format as "Month Day" (e.g., "November 26th")
      else {
        const day = notificationDate.getDate();
        const month = notificationDate.toLocaleString('en-US', { month: 'long' });
        const daySuffix = this.getDaySuffix(day);
        groupLabel = `${month} ${day}${daySuffix}`;
      }

      if (!grouped.has(groupLabel)) {
        grouped.set(groupLabel, []);
      }
      grouped.get(groupLabel)!.push(notification);
    });

    // Convert map to array and sort
    const result = Array.from(grouped.entries()).map(([label, items]) => ({
      label,
      notifications: items.sort((a, b) => a.start - b.start) // Sort by start time within group
    }));

    // Sort groups: Today first, Tomorrow second, then by date
    result.sort((a, b) => {
      if (a.label === 'Today') return -1;
      if (b.label === 'Today') return 1;
      if (a.label === 'Tomorrow') return -1;
      if (b.label === 'Tomorrow') return 1;
      
      // For other dates, compare by the date value
      const dateA = this.getDateFromLabel(a.label);
      const dateB = this.getDateFromLabel(b.label);
      return dateA.getTime() - dateB.getTime();
    });

    return result;
  }

  private getDaySuffix(day: number): string {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  private getDateFromLabel(label: string): Date {
    if (label === 'Today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    }
    if (label === 'Tomorrow') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      return tomorrow;
    }
    
    // Parse "Month Day" format (e.g., "November 26th")
    const match = label.match(/(\w+)\s+(\d+)/);
    if (match) {
      const monthName = match[1];
      const day = parseInt(match[2], 10);
      const year = new Date().getFullYear();
      const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
      return new Date(year, monthIndex, day);
    }
    
    return new Date();
  }
}

